import type { LocaleSetting, LocaleType } from '/#/config';

import { defineStore } from 'pinia';
import { store } from '@/store';

import { LOCALE_KEY } from '@/enums/cacheEnum';
import { createStorage } from '@/utils/Storage';
import { dateZhCN, dateEnUS } from 'naive-ui';
import { naive as naiveEnUS } from '@/locales/en-US/index';
import { naive as naiveZhCN } from '@/locales/zh-CN/index';

const ls = createStorage({ prefixKey: LOCALE_KEY });

const lsLocaleSetting = (ls.get(LOCALE_KEY) as LocaleSetting) || {
  locale: 'en-US',
  // default language
  fallback: 'en-US',
  // available Locales
  availableLocales: ['en-US', 'zh-CN'],
};
interface LocaleState {
  localInfo: LocaleSetting;
}

export const useLocaleStore = defineStore({
  id: 'app-locale',
  state: (): LocaleState => ({
    localInfo: lsLocaleSetting,
  }),
  getters: {
    getLocale(): LocaleType {
      return this.localInfo?.locale ?? 'en-US';
    },
    getNaiveLocale() {
      const localeMap = {
        'zh-CN': { locale: naiveZhCN, date: dateZhCN },
        'en-US': { locale: naiveEnUS, date: dateEnUS },
      };
      const locale: LocaleType = this.getLocale;
      const currentLocale = localeMap[locale];
      return currentLocale;
    },
  },

  actions: {
    /**
     * Set up multilingual information and cache
     * @param info multilingual info
     */
    setLocaleInfo(info: Partial<LocaleSetting>, i18n?: any) {
      this.localInfo = { ...this.localInfo, ...info };
      ls.set(LOCALE_KEY, this.localInfo);
      if (i18n) {
        i18n.locale.value = this.localInfo.locale;
      }
    },
    /**
     * Initialize multilingual information and load the existing configuration from the local cache
     */
    initLocale() {
      this.setLocaleInfo({
        ...this.localInfo,
      });
    },
  },
});

// Need to be used outside the setup
export function useLocaleStoreWithOut() {
  return useLocaleStore(store);
}
